import { normalizeManticoreSeverity } from 'modules/basilisk/basilisk.model';
import { JobStatusLabel } from 'services/jobs/jobs.model';
import { BASILISK_DEFAULT_SCENARIOS, BASILISK_FAILURE_MESSAGE } from '../basilisk/basilisk.const';
import {
	fictitiousName,
	hashSeed,
	MockJobRosterEntry,
	SeededRandom,
	seededRandom,
} from '../shared/mock-jobs-roster.const';
import {
	admissionResult,
	DAY_MS,
	FT,
	HOUR_MS,
	MINUTE_MS,
	MOCK_EPOCH_MS,
	MockJobDocumentSeed,
	MockJobProfile,
	pdf,
} from './jobs-profile.const';

/**
 * Perfis dos jobs gerados do roster (histórico de 90 dias): documentos, tempos, falha,
 * metadados e resultado da extração, sorteados de forma determinística pelo id do job.
 * Os dados pessoais, CPFs e números de processo são FICTÍCIOS (dígitos verificadores válidos).
 */

const generatedProfiles = new Map<string, MockJobProfile>();

export function generatedProfileOf(entry: MockJobRosterEntry): MockJobProfile {
	const cached = generatedProfiles.get(entry.id);
	if (cached) return cached;
	const random = seededRandom(hashSeed(`profile-${entry.id}`));
	const profile =
		entry.product === 'cadastro'
			? cadastroProfile(entry, random)
			: entry.product === 'contestacao'
				? contestacaoProfile(entry, random)
				: basiliskProfile(entry, random);
	generatedProfiles.set(entry.id, profile);
	return profile;
}

// ── Formatação ──────────────────────────────────────────────────────────────

const pad = (value: number, length = 2): string => String(value).padStart(length, '0');

function digitString(random: SeededRandom, length: number): string {
	return Array.from({ length }, () => random.int(0, 9)).join('');
}

function formatDate(ms: number): string {
	const date = new Date(ms);
	return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}

function formatCompetence(ms: number): string {
	const date = new Date(ms);
	return `${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}

/** Mês anterior ao de `ms`, `monthsBack` vezes */
function monthsBefore(ms: number, monthsBack: number): number {
	const date = new Date(ms);
	return new Date(date.getFullYear(), date.getMonth() - monthsBack, 15).getTime();
}

function brl(value: number): string {
	return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function slugOf(name: string): string {
	return name
		.normalize('NFD')
		.replace(/[̀-ͯ]/g, '')
		.toLowerCase()
		.split(' ')
		.filter((_, index, parts) => index === 0 || index === parts.length - 1)
		.join('-');
}

function cpfOf(random: SeededRandom): string {
	const digits = Array.from({ length: 9 }, () => random.int(0, 9));
	for (const length of [9, 10]) {
		const sum = digits
			.slice(0, length)
			.reduce((total, digit, index) => total + digit * (length + 1 - index), 0);
		digits.push(((sum * 10) % 11) % 10);
	}
	const text = digits.join('');
	return `${text.slice(0, 3)}.${text.slice(3, 6)}.${text.slice(6, 9)}-${text.slice(9)}`;
}

function rgOf(random: SeededRandom, uf: string): string {
	const text = digitString(random, 9);
	return `${text.slice(0, 2)}.${text.slice(2, 5)}.${text.slice(5, 8)}-${text.slice(8)} SSP/${uf}`;
}

function pisOf(random: SeededRandom): string {
	const text = `1${digitString(random, 10)}`;
	return `${text.slice(0, 3)}.${text.slice(3, 8)}.${text.slice(8, 10)}-${text.slice(10)}`;
}

function ctpsOf(random: SeededRandom, uf: string): string {
	return `${digitString(random, 7)} — série ${pad(random.int(1, 99), 4)}/${uf}`;
}

/** Resto de um número decimal grande (em texto) por um divisor pequeno */
function modOf(digits: string, divisor: number): number {
	return [...digits].reduce((rest, digit) => (rest * 10 + Number(digit)) % divisor, 0);
}

const TRT_ORIGINS = [
	{ region: '02', maxOrigin: 90 },
	{ region: '15', maxOrigin: 150 },
	{ region: '01', maxOrigin: 80 },
	{ region: '03', maxOrigin: 48 },
	{ region: '04', maxOrigin: 30 },
	{ region: '09', maxOrigin: 28 },
] as const;

/** Número CNJ fictício (NNNNNNN-DD.AAAA.5.TR.OOOO) com dígito verificador válido (módulo 97) */
function cnjOf(random: SeededRandom): string {
	const trt = random.pick(TRT_ORIGINS);
	const sequence = pad(random.int(10_000, 12_999), 7);
	const year = '2026';
	const origin = pad(random.int(1, trt.maxOrigin), 4);
	const check = 98 - modOf(`${sequence}${year}5${trt.region}${origin}00`, 97);
	return `${sequence}-${pad(check)}.${year}.5.${trt.region}.${origin}`;
}

// ── Cadastro ────────────────────────────────────────────────────────────────

interface RoleSeed {
	cargo: string;
	cbo: string;
	departamento: string;
	salary: readonly [number, number];
}

const ROLES: readonly RoleSeed[] = [
	{
		cargo: 'Agente de Aeroporto',
		cbo: '4221-05',
		departamento: 'Atendimento — Terminal 2',
		salary: [3_100, 3_900],
	},
	{
		cargo: 'Auxiliar de Rampa',
		cbo: '7832-25',
		departamento: 'Operações — Pátio',
		salary: [2_400, 2_950],
	},
	{
		cargo: 'Mecânico de Aeronaves',
		cbo: '9141-05',
		departamento: 'Manutenção — Hangar 3',
		salary: [6_200, 8_400],
	},
	{
		cargo: 'Comissário de Bordo',
		cbo: '5111-10',
		departamento: 'Tripulação — Base GRU',
		salary: [3_900, 5_200],
	},
	{
		cargo: 'Analista de Departamento Pessoal',
		cbo: '4110-10',
		departamento: 'Gente & Gestão',
		salary: [4_200, 5_600],
	},
	{
		cargo: 'Assistente Administrativo',
		cbo: '4110-10',
		departamento: 'Administrativo',
		salary: [2_600, 3_300],
	},
	{
		cargo: 'Analista de Planejamento',
		cbo: '2522-10',
		departamento: 'Planejamento de Malha',
		salary: [6_800, 8_900],
	},
	{
		cargo: 'Técnico de Segurança do Trabalho',
		cbo: '3516-05',
		departamento: 'SESMT',
		salary: [4_800, 6_100],
	},
	{
		cargo: 'Operador de Check-in',
		cbo: '4221-05',
		departamento: 'Atendimento — Terminal 1',
		salary: [2_900, 3_400],
	},
	{
		cargo: 'Supervisor de Operações',
		cbo: '4101-05',
		departamento: 'Operações — Solo',
		salary: [7_400, 9_800],
	},
];

const ADDRESSES = [
	{
		logradouro: 'Rua das Palmeiras',
		bairro: 'Jardim Aeroporto',
		cidade: 'Guarulhos',
		uf: 'SP',
		cep: '07140',
	},
	{
		logradouro: 'Av. dos Imigrantes',
		bairro: 'Vila Nova',
		cidade: 'São Paulo',
		uf: 'SP',
		cep: '04329',
	},
	{ logradouro: 'Rua Santa Clara', bairro: 'Centro', cidade: 'Campinas', uf: 'SP', cep: '13015' },
	{
		logradouro: 'Rua dos Ipês',
		bairro: 'Jardim Botânico',
		cidade: 'Rio de Janeiro',
		uf: 'RJ',
		cep: '22461',
	},
	{
		logradouro: 'Av. Getúlio Vargas',
		bairro: 'Funcionários',
		cidade: 'Belo Horizonte',
		uf: 'MG',
		cep: '30112',
	},
	{
		logradouro: 'Rua Sete de Setembro',
		bairro: 'Centro',
		cidade: 'Curitiba',
		uf: 'PR',
		cep: '80060',
	},
	{
		logradouro: 'Rua Mato Grosso',
		bairro: 'Cidade Jardim',
		cidade: 'São José dos Pinhais',
		uf: 'PR',
		cep: '83035',
	},
] as const;

const MEDICAL_CAUSES = [
	'J11 — Influenza',
	'M54.5 — Dor lombar baixa',
	'S93.4 — Entorse do tornozelo',
	'A09 — Diarreia e gastroenterite',
	'J06.9 — Infecção aguda das vias aéreas superiores',
	'H10.9 — Conjuntivite',
	'M75.1 — Síndrome do manguito rotador',
	'R51 — Cefaleia',
] as const;

const CADASTRO_FAILURES = [
	{
		reason: 'LOW_IMAGE_QUALITY',
		message:
			'A digitalização está abaixo da resolução mínima (72 dpi). Reenvie os arquivos com pelo menos 200 dpi.',
	},
	{
		reason: 'DOCUMENT_UNREADABLE',
		message:
			'Um dos arquivos está protegido por senha e não pôde ser lido. Envie o arquivo sem proteção.',
	},
	{
		reason: 'UNSUPPORTED_DOCUMENT',
		message:
			'O tipo de documento não foi reconhecido pelo prompt configurado. Confira o tipo informado no envio.',
	},
	{
		reason: 'EXTRACTION_TIMEOUT',
		message: 'A extração excedeu o tempo limite de 10 minutos. Tente reprocessar o job.',
	},
] as const;

type CadastroKind =
	| 'admissao'
	| 'ctps'
	| 'rescisao'
	| 'holerites'
	| 'experiencia'
	| 'ferias'
	| 'ponto'
	| 'atestado'
	| 'loteAdmissional'
	| 'loteAtestados'
	| 'loteHolerites';

/** Prefixos das descrições geradas no roster (`CADASTRO_KINDS`) */
const CADASTRO_KIND_PREFIXES: readonly (readonly [string, CadastroKind])[] = [
	['Ficha de admissão', 'admissao'],
	['CTPS', 'ctps'],
	['Rescisão', 'rescisao'],
	['Holerites', 'holerites'],
	['Contrato de experiência', 'experiencia'],
	['Aviso e recibo de férias', 'ferias'],
	['Cartões de ponto', 'ponto'],
	['Atestado médico', 'atestado'],
];

function cadastroKindOf(description: string): CadastroKind {
	if (description.startsWith('Lote API')) {
		if (description.includes('atestados')) return 'loteAtestados';
		if (description.includes('holerites')) return 'loteHolerites';
		return 'loteAdmissional';
	}
	return (
		CADASTRO_KIND_PREFIXES.find(([prefix]) => description.startsWith(prefix))?.[1] ?? 'admissao'
	);
}

interface CadastroContent {
	documents: MockJobDocumentSeed[];
	result: Record<string, unknown>;
}

interface CadastroContext {
	random: SeededRandom;
	name: string;
	slug: string;
	createdMs: number;
	lotSize: number;
}

function salaryOf(random: SeededRandom, role: RoleSeed): number {
	return Math.round(random.int(role.salary[0], role.salary[1]) / 10) * 10;
}

function sizeOf(random: SeededRandom, pages: number): number {
	return pages * random.int(90_000, 260_000) + random.int(1_000, 40_000);
}

function admissionContent(
	{ random, name, slug, createdMs }: CadastroContext,
	withCtps: boolean,
): CadastroContent {
	const role = random.pick(ROLES);
	const address = random.pick(ADDRESSES);
	const dependents = Array.from({ length: random.pick([0, 0, 1, 1, 2]) }, (_, index) => ({
		nome: `${random.pick(['Miguel', 'Alice', 'Theo', 'Helena', 'Davi', 'Laura', 'Heitor', 'Cecília'])} ${name.split(' ').slice(-1)[0]}`,
		parentesco: index === 0 && random.chance(0.4) ? 'Cônjuge' : random.pick(['Filho', 'Filha']),
		nascimento: `${pad(random.int(1, 28))}/${pad(random.int(1, 12))}/${random.int(2012, 2024)}`,
	}));
	const documents = withCtps
		? [
				pdf(`ctps-${slug}.pdf`, FT.documentoIdentidade, 4, sizeOf(random, 4), 'CTPS'),
				pdf(`contrato-trabalho-${slug}.pdf`, FT.contratoTrabalho, 6, sizeOf(random, 6)),
			]
		: [
				pdf(
					`ficha-admissao-${slug}.pdf`,
					FT.contratoTrabalho,
					3,
					sizeOf(random, 3),
					'Ficha de admissão',
				),
				pdf(`rg-cpf-${slug}.pdf`, FT.documentoIdentidade, 1, sizeOf(random, 1)),
				...(random.chance(0.5)
					? [
							pdf(
								`comprovante-residencia-${slug}.pdf`,
								FT.comprovanteResidencia,
								1,
								sizeOf(random, 1),
							),
						]
					: []),
			];
	return {
		documents,
		result: admissionResult({
			nome: name,
			cpf: cpfOf(random),
			rg: rgOf(random, address.uf),
			nascimento: `${pad(random.int(1, 28))}/${pad(random.int(1, 12))}/${random.int(1972, 2004)}`,
			admissao: formatDate(createdMs + random.int(2, 20) * DAY_MS),
			cargo: role.cargo,
			cbo: role.cbo,
			departamento: role.departamento,
			salario: brl(salaryOf(random, role)),
			pis: pisOf(random),
			ctps: ctpsOf(random, address.uf),
			endereco: {
				logradouro: `${address.logradouro}, ${random.int(12, 2400)}`,
				bairro: address.bairro,
				cidade: address.cidade,
				uf: address.uf,
				cep: `${address.cep}-${digitString(random, 3)}`,
			},
			dependentes: dependents,
		}),
	};
}

const TERMINATION_REASONS = [
	'Dispensa sem justa causa',
	'Dispensa sem justa causa',
	'Pedido de demissão',
	'Acordo entre as partes (art. 484-A da CLT)',
	'Término do contrato de experiência',
] as const;

function terminationContent({ random, name, slug, createdMs }: CadastroContext): CadastroContent {
	const role = random.pick(ROLES);
	const salary = salaryOf(random, role);
	const yearsWorked = random.int(1, 9);
	const reason = random.pick(TERMINATION_REASONS);
	const dismissal = reason === 'Dispensa sem justa causa';
	const agreement = reason.startsWith('Acordo');
	const endMs = createdMs - random.int(2, 12) * DAY_MS;
	const endDay = new Date(endMs).getDate();
	const monthsInYear = new Date(endMs).getMonth() + 1;
	const noticeDays = Math.min(90, 30 + 3 * yearsWorked);

	const balance = (salary / 30) * endDay;
	const proportionalVacation = (salary / 12) * random.int(1, 11) * (4 / 3);
	const proportionalThirteenth = (salary / 12) * monthsInYear;
	const notice = dismissal
		? (salary / 30) * noticeDays
		: agreement
			? (salary / 30) * noticeDays * 0.5
			: 0;
	const fgtsBalance = salary * 0.08 * 12 * yearsWorked;
	const fgtsFine = dismissal ? fgtsBalance * 0.4 : agreement ? fgtsBalance * 0.2 : 0;
	const total = balance + proportionalVacation + proportionalThirteenth + notice + fgtsFine;

	return {
		documents: [
			pdf(`trct-${slug}.pdf`, FT.termoRescisao, 2, sizeOf(random, 2)),
			pdf(`aviso-previo-${slug}.pdf`, FT.termoRescisao, 1, sizeOf(random, 1), 'Aviso prévio'),
			...(dismissal || agreement
				? [pdf(`extrato-fgts-${slug}.pdf`, FT.subsidio, 3, sizeOf(random, 3), 'Extrato do FGTS')]
				: []),
		],
		result: {
			nome_completo: name,
			cpf: cpfOf(random),
			data_admissao: formatDate(endMs - yearsWorked * 365 * DAY_MS - random.int(0, 200) * DAY_MS),
			data_desligamento: formatDate(endMs),
			cargo: role.cargo,
			motivo_rescisao: reason,
			aviso_previo: dismissal
				? `Indenizado — ${noticeDays} dias`
				: agreement
					? 'Indenizado pela metade'
					: 'Trabalhado',
			ultimo_salario: brl(salary),
			verbas_rescisorias: {
				saldo_salario: brl(balance),
				ferias_proporcionais_com_terco: brl(proportionalVacation),
				decimo_terceiro_proporcional: brl(proportionalThirteenth),
				aviso_previo_indenizado: brl(notice),
				multa_fgts: brl(fgtsFine),
			},
			total_bruto: brl(total),
			homologado: random.chance(0.8),
		},
	};
}

function payslipCompetences(
	random: SeededRandom,
	salary: number,
	createdMs: number,
): Record<string, string>[] {
	return [3, 2, 1].map((monthsBack) => {
		const gross = salary + (random.chance(0.4) ? random.int(150, 900) : 0);
		const deductions = gross * (random.int(170, 230) / 1_000);
		return {
			mes: formatCompetence(monthsBefore(createdMs, monthsBack)),
			salario_bruto: brl(gross),
			descontos: brl(deductions),
			liquido: brl(gross - deductions),
		};
	});
}

function payslipsContent({ random, name, slug, createdMs }: CadastroContext): CadastroContent {
	const role = random.pick(ROLES);
	const competences = payslipCompetences(random, salaryOf(random, role), createdMs);
	return {
		documents: competences.map((competence) =>
			pdf(
				`holerite-${competence['mes'].replace('/', '-')}-${slug}.pdf`,
				FT.holerite,
				1,
				sizeOf(random, 1),
			),
		),
		result: { colaborador: name, cpf: cpfOf(random), cargo: role.cargo, competencias: competences },
	};
}

function probationContent({ random, name, slug, createdMs }: CadastroContext): CadastroContent {
	const role = random.pick(ROLES);
	const startMs = createdMs - random.int(0, 6) * DAY_MS;
	return {
		documents: [pdf(`contrato-experiencia-${slug}.pdf`, FT.contratoTrabalho, 5, sizeOf(random, 5))],
		result: {
			nome_completo: name,
			cpf: cpfOf(random),
			cargo: role.cargo,
			tipo_contrato: 'Contrato de experiência',
			data_inicio: formatDate(startMs),
			prazo_experiencia: '45 dias, prorrogável por mais 45',
			data_termino_previsto: formatDate(startMs + 44 * DAY_MS),
			salario_base: brl(salaryOf(random, role)),
			jornada: random.pick(['44h semanais', '40h semanais', 'Escala 12x36']),
			local_trabalho: role.departamento,
			clausula_prorrogacao: true,
		},
	};
}

function vacationContent({ random, name, slug, createdMs }: CadastroContext): CadastroContent {
	const role = random.pick(ROLES);
	const salary = salaryOf(random, role);
	const startMs = createdMs + random.int(30, 45) * DAY_MS;
	const days = random.pick([30, 30, 20, 15]);
	const acquisitionEnd = new Date(createdMs).getFullYear();
	return {
		documents: [
			pdf(`aviso-ferias-${slug}.pdf`, FT.contratoTrabalho, 1, sizeOf(random, 1), 'Aviso de férias'),
			pdf(`recibo-ferias-${slug}.pdf`, FT.holerite, 1, sizeOf(random, 1), 'Recibo de férias'),
		],
		result: {
			colaborador: name,
			cpf: cpfOf(random),
			periodo_aquisitivo: `${acquisitionEnd - 1}/${acquisitionEnd}`,
			inicio_gozo: formatDate(startMs),
			fim_gozo: formatDate(startMs + (days - 1) * DAY_MS),
			dias_gozo: days,
			abono_pecuniario: days === 20 ? '10 dias' : 'Não',
			valor_ferias: brl((salary / 30) * days),
			terco_constitucional: brl(((salary / 30) * days) / 3),
		},
	};
}

function timesheetContent({ random, name, slug, createdMs }: CadastroContext): CadastroContent {
	const months = [3, 2, 1].map((monthsBack) =>
		formatCompetence(monthsBefore(createdMs, monthsBack)),
	);
	return {
		documents: months.map((month) =>
			pdf(
				`cartao-ponto-${month.replace('/', '-')}-${slug}.pdf`,
				FT.cartaoPonto,
				2,
				sizeOf(random, 2),
			),
		),
		result: {
			colaborador: name,
			matricula: digitString(random, 6),
			competencias: months.map((month) => ({
				mes: month,
				horas_trabalhadas: `${random.int(176, 212)}h${pad(random.int(0, 59))}`,
				horas_extras_50: `${random.int(0, 24)}h${pad(random.int(0, 59))}`,
				adicional_noturno: `${random.int(0, 40)}h${pad(random.int(0, 59))}`,
				faltas: random.pick([0, 0, 0, 1, 2]),
			})),
		},
	};
}

function medicalCertificate(
	random: SeededRandom,
	name: string,
	createdMs: number,
): Record<string, unknown> {
	return {
		colaborador: name,
		cid: random.pick(MEDICAL_CAUSES),
		data_inicio: formatDate(createdMs - random.int(1, 5) * DAY_MS),
		dias_afastamento: random.pick([1, 2, 3, 3, 5, 7, 10, 15]),
		medico: `${random.pick(['Dr.', 'Dra.'])} ${fictitiousName(random).split(' ').slice(0, 2).join(' ')}`,
		crm: `CRM/${random.pick(['SP', 'SP', 'RJ', 'PR'])} ${random.int(40, 210)}.${pad(random.int(0, 999), 3)}`,
	};
}

function medicalCertificateContent({
	random,
	name,
	slug,
	createdMs,
}: CadastroContext): CadastroContent {
	return {
		documents: [pdf(`atestado-${slug}.pdf`, FT.atestadoMedico, 1, sizeOf(random, 1))],
		result: { atestados: [medicalCertificate(random, name, createdMs)] },
	};
}

function admissionLotContent({ random, createdMs, lotSize }: CadastroContext): CadastroContent {
	const pagesPerDocument = random.int(2, 4);
	return {
		documents: Array.from({ length: lotSize }, (_, index) =>
			pdf(
				`lote-admissional-${pad(index + 1)}.pdf`,
				FT.contratoTrabalho,
				pagesPerDocument,
				sizeOf(random, pagesPerDocument),
				'Ficha de admissão',
			),
		),
		result: {
			total_documentos: lotSize,
			colaboradores: Array.from({ length: Math.min(lotSize, 5) }, () => ({
				nome: fictitiousName(random),
				cpf: cpfOf(random),
				cargo: random.pick(ROLES).cargo,
				data_admissao: formatDate(createdMs + random.int(3, 15) * DAY_MS),
			})),
			pendencias: random.chance(0.5)
				? [`${random.int(1, 3)} ficha(s) sem assinatura do colaborador`]
				: [],
		},
	};
}

function certificateLotContent({ random, createdMs, lotSize }: CadastroContext): CadastroContent {
	return {
		documents: Array.from({ length: lotSize }, (_, index) =>
			pdf(`atestado-lote-${pad(index + 1)}.pdf`, FT.atestadoMedico, 1, sizeOf(random, 1)),
		),
		result: {
			total_documentos: lotSize,
			atestados: Array.from({ length: Math.min(lotSize, 4) }, () =>
				medicalCertificate(random, fictitiousName(random), createdMs),
			),
		},
	};
}

function payslipLotContent({ random, createdMs, lotSize }: CadastroContext): CadastroContent {
	const competence = formatCompetence(monthsBefore(createdMs, 1));
	const samples = Array.from({ length: 3 }, () => {
		const role = random.pick(ROLES);
		return {
			colaborador: fictitiousName(random),
			cargo: role.cargo,
			salario_bruto: brl(salaryOf(random, role)),
		};
	});
	return {
		documents: Array.from({ length: lotSize }, (_, index) =>
			pdf(
				`holerite-${competence.replace('/', '-')}-${pad(index + 1, 3)}.pdf`,
				FT.holerite,
				1,
				sizeOf(random, 1),
			),
		),
		result: {
			competencia: competence,
			total_colaboradores: lotSize,
			folha_bruta_total: brl(lotSize * random.int(3_800, 5_600)),
			amostra: samples,
		},
	};
}

const CADASTRO_BUILDERS: Record<CadastroKind, (context: CadastroContext) => CadastroContent> = {
	admissao: (context) => admissionContent(context, false),
	ctps: (context) => admissionContent(context, true),
	rescisao: terminationContent,
	holerites: payslipsContent,
	experiencia: probationContent,
	ferias: vacationContent,
	ponto: timesheetContent,
	atestado: medicalCertificateContent,
	loteAdmissional: admissionLotContent,
	loteAtestados: certificateLotContent,
	loteHolerites: payslipLotContent,
};

function totalPages(documents: MockJobDocumentSeed[]): number {
	return documents.reduce((total, document) => total + document.pageCount, 0);
}

function createdMsOf(entry: MockJobRosterEntry): number {
	return MOCK_EPOCH_MS - entry.createdDaysAgo * DAY_MS;
}

function cadastroProfile(entry: MockJobRosterEntry, random: SeededRandom): MockJobProfile {
	const name = entry.description.split(' — ')[1] ?? fictitiousName(random);
	const lotSize = Number(/\d+/.exec(entry.description)?.[0] ?? 10);
	const { documents, result } = CADASTRO_BUILDERS[cadastroKindOf(entry.description)]({
		random,
		name,
		slug: slugOf(name),
		createdMs: createdMsOf(entry),
		lotSize,
	});
	const isApi = entry.source === 'API_KEY';
	return {
		minutesAgo: random.int(15, 600),
		queueMs: isApi ? random.int(5_000, 40_000) : random.int(1_200, 6_000),
		processingMs: Math.min(
			15 * MINUTE_MS,
			15_000 + totalPages(documents) * 2_200 + random.int(0, 12_000),
		),
		reviewMs: 0,
		documents,
		failure: entry.status === 'Falha' ? { ...random.pick(CADASTRO_FAILURES) } : undefined,
		result,
	};
}

// ── Contestação ─────────────────────────────────────────────────────────────

/** Subsídios típicos de cada tese (casados pela descrição do job) */
const CONTESTACAO_SUBSIDIES: readonly (readonly [RegExp, readonly string[]])[] = [
	[
		/horas|sobreaviso|interjornada|intrajornada|noturno/i,
		['cartoes-ponto-2024-2026.pdf', 'escalas-trabalho.pdf', 'acordo-compensacao-jornada.pdf'],
	],
	[
		/periculosidade|insalubridade/i,
		['ltcat-2025.pdf', 'ppp-reclamante.pdf', 'fichas-entrega-epi.pdf'],
	],
	[/rescisão indireta/i, ['comprovantes-pagamento-salarios.pdf', 'extrato-fgts.pdf']],
	[
		/equiparação|acúmulo/i,
		['fichas-financeiras-paradigma.pdf', 'plano-cargos-salarios.pdf', 'descricao-cargos.pdf'],
	],
	[
		/dano moral|assédio/i,
		['codigo-conduta.pdf', 'registros-canal-denuncias.pdf', 'atas-reuniao-equipe.pdf'],
	],
	[
		/doença|acidente/i,
		['cat-comunicacao-acidente.pdf', 'aso-periodicos.pdf', 'laudo-medico-ocupacional.pdf'],
	],
	[/vínculo|subsidiária/i, ['contrato-prestacao-servicos.pdf', 'notas-fiscais-prestadora.pdf']],
	[/gestante/i, ['exame-demissional.pdf', 'trct-homologado.pdf']],
	[
		/verbas|fgts|justa causa/i,
		['trct-homologado.pdf', 'comprovante-pagamento-verbas.pdf', 'extrato-fgts.pdf'],
	],
];

const DEFAULT_SUBSIDIES = ['contrato-trabalho-reclamante.pdf', 'fichas-financeiras.pdf'] as const;

const CONTESTACAO_FAILURES = [
	{
		reason: 'DOCUMENT_UNREADABLE',
		message:
			'A petição inicial está protegida por senha e não pôde ser lida. Envie o arquivo sem proteção.',
	},
	{
		reason: 'OCR_LOW_CONFIDENCE',
		message:
			'A petição inicial é uma digitalização sem camada de texto e o OCR não atingiu a confiança mínima. Reenvie uma versão pesquisável.',
	},
	{
		reason: 'MISSING_INITIAL_PETITION',
		message:
			'Nenhum documento foi classificado como petição inicial. Confira o tipo dos arquivos enviados.',
	},
	{
		reason: 'PROCESSING_TIMEOUT',
		message:
			'A geração da contestação excedeu o tempo limite de 15 minutos. Tente reprocessar o job.',
	},
] as const;

function contestacaoProfile(entry: MockJobRosterEntry, random: SeededRandom): MockJobProfile {
	const cnj = cnjOf(random);
	const subsidies =
		CONTESTACAO_SUBSIDIES.find(([pattern]) => pattern.test(entry.description))?.[1] ??
		DEFAULT_SUBSIDIES;
	const petitionPages = random.int(14, 42);
	return {
		minutesAgo: random.int(15, 600),
		queueMs: random.int(2_000, 9_000),
		processingMs: random.int(230_000, 430_000),
		reviewMs: random.int(1, 30) * HOUR_MS + random.int(0, 59) * MINUTE_MS,
		documents: [
			pdf(
				`peticao-inicial-${cnj}.pdf`,
				FT.peticaoInicial,
				petitionPages,
				sizeOf(random, petitionPages),
			),
			...subsidies.map((filename) => {
				const pages = random.int(2, 36);
				return pdf(filename, FT.subsidio, pages, sizeOf(random, pages));
			}),
		],
		failure: entry.status === 'Falha' ? { ...random.pick(CONTESTACAO_FAILURES) } : undefined,
	};
}

// ── Basilisk ────────────────────────────────────────────────────────────────

const BASILISK_FILE_TYPES: Record<string, string> = {
	'Atestado médico': FT.atestadoMedico,
	'Comprovante de residência': FT.comprovanteResidencia,
	Holerite: FT.holerite,
	'Documento digitalizado': FT.documentoIdentidade,
};

/** Status em que o laudo do Manticore já existe (a severidade aparece na lista) */
const ANALYSED_STATUSES: ReadonlySet<JobStatusLabel> = new Set<JobStatusLabel>([
	'Aguardando revisão',
	'Em revisão',
	'Finalizado',
]);

function basiliskProfile(entry: MockJobRosterEntry, random: SeededRandom): MockJobProfile {
	// Mesmo cenário que mocks/basilisk sorteia para o id (laudo e lista batem)
	const scenario =
		BASILISK_DEFAULT_SCENARIOS[hashSeed(entry.id) % BASILISK_DEFAULT_SCENARIOS.length];
	const caseNumber = 4_600 - entry.createdDaysAgo * 11 - random.int(0, 9);
	return {
		minutesAgo: random.int(15, 600),
		queueMs: 4_000,
		processingMs: scenario.processingTimeMs,
		reviewMs: random.int(1, 20) * HOUR_MS + random.int(0, 59) * MINUTE_MS,
		documents: [
			pdf(
				entry.description,
				BASILISK_FILE_TYPES[scenario.fileTypeName] ?? FT.subsidio,
				scenario.pageCount,
				scenario.bytes,
				scenario.fileTypeName,
			),
		],
		failure: entry.status === 'Falha' ? { ...BASILISK_FAILURE_MESSAGE } : undefined,
		metadata: {
			cnj: random.chance(0.85) ? cnjOf(random) : null,
			legalboxContencioso: `LBC-2026-${pad(caseNumber, 6)}`,
			severity: ANALYSED_STATUSES.has(entry.status)
				? normalizeManticoreSeverity(scenario.severity)
				: null,
		},
	};
}
